import { useEffect, useMemo, useState } from 'react';
import { performAction } from '../api/sophia';
import { useAppSelector } from '../hooks/redux';
import { selectCurrentCatalogMode } from '../features/thread/threadSlice';
import { getCollectionFromCatalogMode } from '../types/catalogMode';

type MappingKind = 'tipo-filato' | 'merchandising-plan' | 'negozi';

interface AdminMappingsDashboardProps {
  threadId: string;
  messageId: string;
  onAdminAlertChange?: (hasAlert: boolean) => void;
  onAdminAlertReasonsChange?: (reasons: string[]) => void;
}

const MAPPING_META: Record<
  MappingKind,
  {
    title: string;
    getAction: string;
    setAction: string;
    deleteAction: string;
  }
> = {
  'tipo-filato': {
    title: 'Tipo Filato',
    getAction: 'admin-get-mappings-tipo-filato',
    setAction: 'admin-set-mappings-tipo-filato',
    deleteAction: 'admin-delete-mappings-tipo-filato',
  },
  'merchandising-plan': {
    title: 'Merchandising Plan',
    getAction: 'admin-get-mappings-merchandising-plan',
    setAction: 'admin-set-mappings-merchandising-plan',
    deleteAction: 'admin-delete-mappings-merchandising-plan',
  },
  negozi: {
    title: 'Negozi',
    getAction: 'admin-get-mappings-negozi',
    setAction: 'admin-set-mappings-negozi',
    deleteAction: 'admin-delete-mappings-negozi',
  },
};

const KINDS: MappingKind[] = ['tipo-filato', 'merchandising-plan', 'negozi'];

const EDITABLE_FIELDS: Record<MappingKind, string[]> = {
  'tipo-filato': ['gender', 'source_tipo_filato', 'mapped_tipo_filato'],
  'merchandising-plan': ['gender', 'tipo_filato_ds', 'tipo_filato_mp', 'macro_tema'],
  negozi: ['gender', 'cod_negozio_ds', 'cod_shop_budget', 'cod_negozio_budget'],
};

const EDITABLE_EXISTING_COLUMNS: Record<MappingKind, string[]> = {
  'tipo-filato': ['mapped_tipo_filato'],
  'merchandising-plan': ['tipo_filato_mp'],
  negozi: ['cod_shop_budget', 'cod_negozio_budget'],
};

const REQUIRED_NEW_ROW_COLUMNS: Record<MappingKind, string[]> = {
  'tipo-filato': ['gender', 'source_tipo_filato', 'mapped_tipo_filato'],
  'merchandising-plan': ['gender', 'tipo_filato_ds', 'tipo_filato_mp', 'macro_tema'],
  negozi: ['gender', 'cod_negozio_ds', 'cod_shop_budget', 'cod_negozio_budget'],
};

const prettyJson = (value: unknown): string => {
  try {
    return JSON.stringify(value ?? {}, null, 2);
  } catch {
    return '{}';
  }
};

const safeParse = (value: string): unknown | null => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const toRowObject = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
};

const pickEditableFields = (kind: MappingKind, row: Record<string, unknown>): Record<string, unknown> => {
  const next: Record<string, unknown> = {};
  for (const field of EDITABLE_FIELDS[kind]) {
    if (Object.prototype.hasOwnProperty.call(row, field)) {
      next[field] = row[field];
    }
  }
  return next;
};

const trimString = (value: unknown): string => String(value ?? '').trim();

const buildTipoFilatoPayload = (row: Record<string, unknown>) => {
  const gender = trimString(row.gender);
  const sourceTipoFilato = trimString(row.source_tipo_filato);
  if (!gender || !sourceTipoFilato) return null;
  const mappedRaw = trimString(row.mapped_tipo_filato);
  return {
    gender,
    source_tipo_filato: sourceTipoFilato,
    mapped_tipo_filato: mappedRaw === '' ? null : mappedRaw,
  };
};

const buildMerchandisingPayloadRow = (row: Record<string, unknown>) => {
  const gender = trimString(row.gender);
  const tipoFilatoDs = trimString(row.tipo_filato_ds);
  const tipoFilatoMp = trimString(row.tipo_filato_mp);
  const macroTema = trimString(row.macro_tema);
  if (!gender || !tipoFilatoDs || !tipoFilatoMp || !macroTema) return null;
  return {
    gender,
    tipo_filato_ds: tipoFilatoDs,
    tipo_filato_mp: tipoFilatoMp,
    macro_tema: macroTema,
  };
};

const buildNegoziPayloadRow = (row: Record<string, unknown>) => {
  const gender = trimString(row.gender);
  const codNegozioDs = trimString(row.cod_negozio_ds);
  const codShopBudget = trimString(row.cod_shop_budget);
  const codNegozioBudget = trimString(row.cod_negozio_budget);
  if (!gender || !codNegozioDs || !codShopBudget || !codNegozioBudget) return null;
  return {
    gender,
    cod_negozio_ds: codNegozioDs,
    cod_shop_budget: codShopBudget,
    cod_negozio_budget: codNegozioBudget,
  };
};

const isNewRow = (row: Record<string, unknown>): boolean => row.__isNewRow === true;
const isInternalColumn = (col: string): boolean => col.startsWith('__');
const toObjectRows = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value)
    ? value.map(toRowObject).filter((row): row is Record<string, unknown> => row !== null)
    : [];

const isTruthyFlag = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
  }
  return false;
};

const hasTipoFilatoRedRows = (value: unknown): boolean => {
  if (!Array.isArray(value)) return false;
  return value.some((entry) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return false;
    const row = entry as Record<string, unknown>;
    return isTruthyFlag(row.is_new) && isTruthyFlag(row.is_default);
  });
};

const getTipoFilatoRowKey = (row: { gender: string; source_tipo_filato: string }) =>
  `${row.gender}||${row.source_tipo_filato}`;
const getMerchRowKey = (row: { gender: string; tipo_filato_ds: string }) =>
  `${row.gender}||${row.tipo_filato_ds}`;
const getNegoziRowKey = (row: { gender: string; cod_negozio_ds: string }) =>
  `${row.gender}||${row.cod_negozio_ds}`;
const mappingsBaseUrl = (import.meta.env.VITE_MAPPINGS_API_URL as string | undefined)?.replace(/\/$/, '') ?? 'http://0.0.0.0:8801';
const NON_PROBLEMATIC_SHOPS_STORAGE_KEY = 'admin-non-problematic-missing-shops';

export default function AdminMappingsDashboard({
  threadId,
  messageId,
  onAdminAlertChange,
  onAdminAlertReasonsChange,
}: AdminMappingsDashboardProps) {
  const [gender, setGender] = useState<string>('');
  const currentCatalogMode = useAppSelector(selectCurrentCatalogMode);
  const [loadingAll, setLoadingAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mappings, setMappings] = useState<Record<MappingKind, unknown>>({
    'tipo-filato': {},
    'merchandising-plan': {},
    negozi: {},
  });
  const [drafts, setDrafts] = useState<Record<MappingKind, string>>({
    'tipo-filato': '{}',
    'merchandising-plan': '{}',
    negozi: '{}',
  });
  const [saving, setSaving] = useState<Record<MappingKind, boolean>>({
    'tipo-filato': false,
    'merchandising-plan': false,
    negozi: false,
  });
  const [deleting, setDeleting] = useState<Record<MappingKind, boolean>>({
    'tipo-filato': false,
    'merchandising-plan': false,
    negozi: false,
  });
  const [editing, setEditing] = useState<Record<MappingKind, boolean>>({
    'tipo-filato': false,
    'merchandising-plan': false,
    negozi: false,
  });
  const [statusByKind, setStatusByKind] = useState<Record<MappingKind, string>>({
    'tipo-filato': '',
    'merchandising-plan': '',
    negozi: '',
  });
  const [searchByKind, setSearchByKind] = useState<Record<MappingKind, string>>({
    'tipo-filato': '',
    'merchandising-plan': '',
    negozi: '',
  });
  const [showProblematicByKind, setShowProblematicByKind] = useState<Record<MappingKind, boolean>>({
    'tipo-filato': false,
    'merchandising-plan': false,
    negozi: false,
  });
  const [missingByKind, setMissingByKind] = useState<Record<MappingKind, Record<string, unknown>[]>>({
    'tipo-filato': [],
    'merchandising-plan': [],
    negozi: [],
  });
  const [expandedMissingByKind, setExpandedMissingByKind] = useState<Record<MappingKind, boolean>>({
    'tipo-filato': false,
    'merchandising-plan': false,
    negozi: false,
  });
  const [nonProblematicMissingShops, setNonProblematicMissingShops] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = window.localStorage.getItem(NON_PROBLEMATIC_SHOPS_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return;
      const entries = parsed
        .filter((item): item is string => typeof item === 'string')
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
      setNonProblematicMissingShops(new Set(entries));
    } catch {
      // Ignore corrupted local storage payloads
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(
        NON_PROBLEMATIC_SHOPS_STORAGE_KEY,
        JSON.stringify(Array.from(nonProblematicMissingShops)),
      );
    } catch {
      // Ignore write failures
    }
  }, [nonProblematicMissingShops]);

  const hasAnyAdminAlerts = (
    tipoFilatoRows: unknown,
    merchMissing: Record<string, unknown>[],
    negoziMissing: Record<string, unknown>[],
  ): boolean => hasTipoFilatoRedRows(tipoFilatoRows) || merchMissing.length > 0 || negoziMissing.length > 0;
  const getTipoFilatoAlertCount = (value: unknown): number => {
    if (!Array.isArray(value)) return 0;
    return value.filter((entry) => {
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return false;
      const row = entry as Record<string, unknown>;
      return isTruthyFlag(row.is_new) && isTruthyFlag(row.is_default);
    }).length;
  };

  const getMissingItemKey = (kind: MappingKind, item: Record<string, unknown>): string => {
    const genderValue = trimString(item.gender);
    if (kind === 'merchandising-plan') {
      return `${genderValue}||${trimString(item.tipo_filato_ds)}`;
    }
    if (kind === 'negozi') {
      return `${genderValue}||${trimString(item.cod_negozio_ds)}`;
    }
    return `${genderValue}||`;
  };

  const activeMissingByKind = useMemo(() => {
    const activeNegozi = missingByKind.negozi.filter(
      (item) => !nonProblematicMissingShops.has(getMissingItemKey('negozi', item))
    );
    return {
      ...missingByKind,
      negozi: activeNegozi,
    };
  }, [missingByKind, nonProblematicMissingShops]);

  const ignoredMissingNegozi = useMemo(
    () =>
      missingByKind.negozi.filter((item) =>
        nonProblematicMissingShops.has(getMissingItemKey('negozi', item))
      ),
    [missingByKind.negozi, nonProblematicMissingShops],
  );

  useEffect(() => {
    const tipoFilatoAlertCount = getTipoFilatoAlertCount(mappings['tipo-filato']);
    const reasons: string[] = [];
    if (tipoFilatoAlertCount > 0) {
      reasons.push(`Tipo Filato: ${tipoFilatoAlertCount} righe problematiche`);
    }
    if (activeMissingByKind['merchandising-plan'].length > 0) {
      reasons.push(`Merchandising Plan: ${activeMissingByKind['merchandising-plan'].length} elementi mancanti`);
    }
    if (activeMissingByKind.negozi.length > 0) {
      reasons.push(`Negozi: ${activeMissingByKind.negozi.length} shop mancanti`);
    }
    onAdminAlertChange?.(
      hasAnyAdminAlerts(
        mappings['tipo-filato'],
        activeMissingByKind['merchandising-plan'],
        activeMissingByKind.negozi,
      )
    );
    onAdminAlertReasonsChange?.(reasons);
  }, [activeMissingByKind, mappings, onAdminAlertChange, onAdminAlertReasonsChange]);

  const parseEntriesAndMissing = (kind: MappingKind, payload: unknown): { entries: Record<string, unknown>[]; missing: Record<string, unknown>[] } => {
    let normalizedPayload = payload;
    if (typeof normalizedPayload === 'string') {
      try {
        normalizedPayload = JSON.parse(normalizedPayload);
      } catch {
        normalizedPayload = {};
      }
    }
    const rootResponse =
      normalizedPayload && typeof normalizedPayload === 'object' ? (normalizedPayload as Record<string, unknown>) : {};
    const response =
      rootResponse.data && typeof rootResponse.data === 'object'
        ? (rootResponse.data as Record<string, unknown>)
        : rootResponse;

    const candidateObjects: Record<string, unknown>[] = [response];
    if (rootResponse !== response) {
      candidateObjects.push(rootResponse);
    }

    const unwrapByKey = (key: string): unknown => {
      for (const source of candidateObjects) {
        const direct = source[key];
        if (direct !== undefined) return direct;
        if (source['admin-mappings'] && typeof source['admin-mappings'] === 'object') {
          const nested = (source['admin-mappings'] as Record<string, unknown>)[key];
          if (nested !== undefined) return nested;
        }
      }
      return undefined;
    };

    if (kind === 'tipo-filato') {
      const rows =
        (Array.isArray(normalizedPayload) ? normalizedPayload : null) ??
        (Array.isArray(response.entries) ? response.entries : null) ??
        (Array.isArray(unwrapByKey('tipo-filato')) ? (unwrapByKey('tipo-filato') as unknown[]) : null) ??
        (Array.isArray(unwrapByKey('admin-mappings-tipo-filato'))
          ? (unwrapByKey('admin-mappings-tipo-filato') as unknown[])
          : null) ??
        [];
      return {
        entries: toObjectRows(rows),
        missing: [],
      };
    }

    const kindKey = kind === 'merchandising-plan' ? 'merchandising-plan' : 'negozi';
    const kindActionKey =
      kind === 'merchandising-plan' ? 'admin-mappings-merchandising-plan' : 'admin-mappings-negozi';
    const actionContainer = unwrapByKey(kindActionKey);
    const kindContainer = unwrapByKey(kindKey);
    const entriesRaw =
      (Array.isArray(response.entries) ? response.entries : null) ??
      (Array.isArray(normalizedPayload) ? normalizedPayload : null) ??
      (Array.isArray(kindContainer) ? (kindContainer as unknown[]) : null) ??
      (Array.isArray(actionContainer) ? (actionContainer as unknown[]) : null) ??
      (actionContainer && typeof actionContainer === 'object' && Array.isArray((actionContainer as Record<string, unknown>).entries)
        ? ((actionContainer as Record<string, unknown>).entries as unknown[])
        : null) ??
      (kindContainer && typeof kindContainer === 'object' && Array.isArray((kindContainer as Record<string, unknown>).entries)
        ? ((kindContainer as Record<string, unknown>).entries as unknown[])
        : null) ??
      [];
    const missingKey = kind === 'merchandising-plan' ? 'missing_tipo_filato_ds' : 'missing_shops';
    const missingRaw =
      (Array.isArray(response[missingKey]) ? (response[missingKey] as unknown[]) : null) ??
      (actionContainer && typeof actionContainer === 'object' && Array.isArray((actionContainer as Record<string, unknown>)[missingKey])
        ? ((actionContainer as Record<string, unknown>)[missingKey] as unknown[])
        : null) ??
      (kindContainer && typeof kindContainer === 'object' && Array.isArray((kindContainer as Record<string, unknown>)[missingKey])
        ? ((kindContainer as Record<string, unknown>)[missingKey] as unknown[])
        : null) ??
      [];

    return {
      entries: toObjectRows(entriesRaw),
      missing: toObjectRows(missingRaw),
    };
  };

  const loadAllMappings = async () => {
    if (!threadId || !messageId) {
      setError('Thread o messageId non disponibili per azioni admin.');
      return;
    }
    setLoadingAll(true);
    setError(null);
    try {
      const fetchFromMappingsApi = async () => {
        const collection = getCollectionFromCatalogMode(currentCatalogMode);
        const withGender = (path: string, includeGender: boolean): string => {
          const qs = new URLSearchParams();
          if (includeGender && gender) {
            qs.set('gender', gender);
          }
          if (collection) {
            qs.set('collection', collection);
          }
          const suffix = qs.toString();
          return suffix ? `${mappingsBaseUrl}${path}?${suffix}` : `${mappingsBaseUrl}${path}`;
        };

        const [tipoFilatoResp, merchandisingResp, negoziResp] = await Promise.all([
          fetch(withGender('/mappings/tipo-filato', false)),
          fetch(withGender('/mappings/merchandising-plan', true)),
          fetch(withGender('/mappings/negozi', true)),
        ]);

        if (!tipoFilatoResp.ok || !merchandisingResp.ok || !negoziResp.ok) {
          throw new Error('Mappings API non disponibile');
        }

        const [tipoFilatoJson, merchandisingJson, negoziJson] = await Promise.all([
          tipoFilatoResp.json(),
          merchandisingResp.json(),
          negoziResp.json(),
        ]);

        return {
          tipoFilatoParsed: parseEntriesAndMissing('tipo-filato', tipoFilatoJson),
          merchandisingParsed: parseEntriesAndMissing('merchandising-plan', merchandisingJson),
          negoziParsed: parseEntriesAndMissing('negozi', negoziJson),
        };
      };

      const getParams = (kind: MappingKind): Record<string, unknown> => {
        if (!gender || kind === 'tipo-filato') return {};
        return { 'admin-gender': gender, gender };
      };

      let tipoFilatoParsed: { entries: Record<string, unknown>[]; missing: Record<string, unknown>[] };
      let merchandisingParsed: { entries: Record<string, unknown>[]; missing: Record<string, unknown>[] };
      let negoziParsed: { entries: Record<string, unknown>[]; missing: Record<string, unknown>[] };

      try {
        const fromApi = await fetchFromMappingsApi();
        tipoFilatoParsed = fromApi.tipoFilatoParsed;
        merchandisingParsed = fromApi.merchandisingParsed;
        negoziParsed = fromApi.negoziParsed;
      } catch {
        const [tipoFilatoResult, merchandisingResult, negoziResult] = await Promise.all([
          performAction({
            threadId,
            messageId,
            actionName: MAPPING_META['tipo-filato'].getAction,
            params: getParams('tipo-filato'),
          }),
          performAction({
            threadId,
            messageId,
            actionName: MAPPING_META['merchandising-plan'].getAction,
            params: getParams('merchandising-plan'),
          }),
          performAction({
            threadId,
            messageId,
            actionName: MAPPING_META.negozi.getAction,
            params: getParams('negozi'),
          }),
        ]);

        try {
          tipoFilatoParsed = parseEntriesAndMissing('tipo-filato', tipoFilatoResult);
          merchandisingParsed = parseEntriesAndMissing('merchandising-plan', merchandisingResult);
          negoziParsed = parseEntriesAndMissing('negozi', negoziResult);
        } catch {
          const legacyParams: Record<string, unknown> = {};
          if (gender) {
            legacyParams['admin-gender'] = gender;
          }
          const legacyResult = await performAction({
            threadId,
            messageId,
            actionName: 'admin-get-mappings',
            params: legacyParams,
          });
          tipoFilatoParsed = parseEntriesAndMissing('tipo-filato', legacyResult);
          merchandisingParsed = parseEntriesAndMissing('merchandising-plan', legacyResult);
          negoziParsed = parseEntriesAndMissing('negozi', legacyResult);
        }
      }

      const nextMappings: Record<MappingKind, unknown> = {
        'tipo-filato': tipoFilatoParsed.entries,
        'merchandising-plan': merchandisingParsed.entries,
        negozi: negoziParsed.entries,
      };
      const nextMissingByKind: Record<MappingKind, Record<string, unknown>[]> = {
        'tipo-filato': [],
        'merchandising-plan': merchandisingParsed.missing,
        negozi: negoziParsed.missing,
      };

      setMappings(nextMappings);
      setMissingByKind(nextMissingByKind);
      setDrafts({
        'tipo-filato': prettyJson(nextMappings['tipo-filato']),
        'merchandising-plan': prettyJson(nextMappings['merchandising-plan']),
        negozi: prettyJson(nextMappings['negozi']),
      });
      setEditing({
        'tipo-filato': false,
        'merchandising-plan': false,
        negozi: false,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore nel caricamento mapping');
    } finally {
      setLoadingAll(false);
    }
  };

  useEffect(() => {
    loadAllMappings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, messageId, gender, currentCatalogMode]);

  const parsedDrafts = useMemo(
    () =>
      Object.fromEntries(
        KINDS.map((kind) => [kind, safeParse(drafts[kind])])
      ) as Record<MappingKind, unknown | null>,
    [drafts]
  );

  const updateDraft = (kind: MappingKind, next: unknown) => {
    setDrafts((prev) => ({ ...prev, [kind]: prettyJson(next) }));
  };

  const isColumnEditable = (kind: MappingKind, col: string, row: Record<string, unknown>) => {
    if (isNewRow(row)) {
      return REQUIRED_NEW_ROW_COLUMNS[kind].includes(col);
    }
    return EDITABLE_EXISTING_COLUMNS[kind].includes(col);
  };

  const buildNewRow = (kind: MappingKind, existingCols: string[]): Record<string, unknown> => {
    const requiredCols = REQUIRED_NEW_ROW_COLUMNS[kind];
    const cols = Array.from(new Set([...existingCols, ...requiredCols]));
    const row: Record<string, unknown> = Object.fromEntries(cols.map((col) => [col, '']));
    row.__isNewRow = true;
    if (requiredCols.includes('gender') && gender) {
      row.gender = gender;
    }
    return row;
  };

  const appendMissingAsNewRow = (kind: 'merchandising-plan' | 'negozi', missingItem: Record<string, unknown>) => {
    const parsed = parsedDrafts[kind];
    const sourceRows = Array.isArray(parsed)
      ? parsed.map(toRowObject).filter((row): row is Record<string, unknown> => row !== null)
      : [];

    const entryKey = kind === 'merchandising-plan'
      ? `${trimString(missingItem.gender)}||${trimString(missingItem.tipo_filato_ds)}`
      : `${trimString(missingItem.gender)}||${trimString(missingItem.cod_negozio_ds)}`;

    const alreadyExists = sourceRows.some((row) => {
      if (kind === 'merchandising-plan') {
        return `${trimString(row.gender)}||${trimString(row.tipo_filato_ds)}` === entryKey;
      }
      return `${trimString(row.gender)}||${trimString(row.cod_negozio_ds)}` === entryKey;
    });

    if (alreadyExists) {
      setStatusByKind((prev) => ({ ...prev, [kind]: 'La riga esiste già in tabella.' }));
      setEditing((prev) => ({ ...prev, [kind]: true }));
      return;
    }

    const nextRow: Record<string, unknown> = kind === 'merchandising-plan'
      ? {
          gender: trimString(missingItem.gender),
          tipo_filato_ds: trimString(missingItem.tipo_filato_ds),
          tipo_filato_mp: '',
          macro_tema: '',
          __isNewRow: true,
        }
      : {
          gender: trimString(missingItem.gender),
          cod_negozio_ds: trimString(missingItem.cod_negozio_ds),
          cod_shop_budget: '',
          cod_negozio_budget: '',
          __isNewRow: true,
        };

    setEditing((prev) => ({ ...prev, [kind]: true }));
    setStatusByKind((prev) => ({ ...prev, [kind]: '' }));
    updateDraft(kind, [...sourceRows, nextRow]);
  };

  const saveMapping = async (kind: MappingKind) => {
    if (!threadId || !messageId) {
      setStatusByKind((prev) => ({ ...prev, [kind]: 'Thread o messageId non disponibili.' }));
      return;
    }
    const parsedPayload = safeParse(drafts[kind]);
    if (parsedPayload === null) {
      setStatusByKind((prev) => ({ ...prev, [kind]: 'JSON non valido' }));
      return;
    }

    setSaving((prev) => ({ ...prev, [kind]: true }));
    setStatusByKind((prev) => ({ ...prev, [kind]: '' }));
    try {
      const rawRows = Array.isArray(parsedPayload) ? parsedPayload : [parsedPayload];
      const rowObjects = rawRows.map(toRowObject).filter((row): row is Record<string, unknown> => row !== null);
      const originalRawRows = Array.isArray(mappings[kind]) ? mappings[kind] : [mappings[kind]];
      const originalRows = originalRawRows
        .map(toRowObject)
        .filter((row): row is Record<string, unknown> => row !== null);

      if (rowObjects.length === 0) {
        setStatusByKind((prev) => ({ ...prev, [kind]: 'Nessuna riga valida da inviare' }));
        return;
      }

      const requiredCols = REQUIRED_NEW_ROW_COLUMNS[kind];
      const incompleteNewRows = rowObjects.filter(
        (row) => isNewRow(row) && requiredCols.some((col) => trimString(row[col]) === '')
      );
      if (incompleteNewRows.length > 0) {
        setStatusByKind((prev) => ({
          ...prev,
          [kind]: `Ci sono ${incompleteNewRows.length} nuove righe incomplete. Compila i campi obbligatori prima di salvare.`,
        }));
        return;
      }

      if (kind === 'tipo-filato') {
        const rows = rowObjects
          .map((row) => buildTipoFilatoPayload(pickEditableFields(kind, row)))
          .filter((row): row is NonNullable<ReturnType<typeof buildTipoFilatoPayload>> => row !== null);
        const originalPayloadRows = originalRows
          .map((row) => buildTipoFilatoPayload(pickEditableFields(kind, row)))
          .filter((row): row is NonNullable<ReturnType<typeof buildTipoFilatoPayload>> => row !== null);

        if (rows.length === 0) {
          setStatusByKind((prev) => ({ ...prev, [kind]: 'Compila almeno gender e source_tipo_filato' }));
          return;
        }

        const originalByKey = new Map(
          originalPayloadRows.map((row) => [getTipoFilatoRowKey(row), JSON.stringify(row)])
        );
        const changedRows = rows.filter(
          (row) => originalByKey.get(getTipoFilatoRowKey(row)) !== JSON.stringify(row)
        );

        if (changedRows.length === 0) {
          setStatusByKind((prev) => ({ ...prev, [kind]: 'Nessuna modifica da salvare' }));
          return;
        }

        for (const rowPayload of changedRows) {
          await performAction({
            threadId,
            messageId,
            actionName: MAPPING_META[kind].setAction,
            params: { 'admin-mapping-payload': rowPayload },
          });
        }
        setStatusByKind((prev) => ({ ...prev, [kind]: `Aggiornato con successo (${changedRows.length} righe)` }));
      } else if (kind === 'merchandising-plan') {
        const rows = rowObjects
          .map((row) => buildMerchandisingPayloadRow(pickEditableFields(kind, row)))
          .filter((row): row is NonNullable<ReturnType<typeof buildMerchandisingPayloadRow>> => row !== null);
        const originalPayloadRows = originalRows
          .map((row) => buildMerchandisingPayloadRow(pickEditableFields(kind, row)))
          .filter((row): row is NonNullable<ReturnType<typeof buildMerchandisingPayloadRow>> => row !== null);
        if (rows.length === 0) {
          setStatusByKind((prev) => ({
            ...prev,
            [kind]: 'Compila tutte le colonne richieste: gender, tipo_filato_ds, tipo_filato_mp, macro_tema',
          }));
          return;
        }

        const originalByKey = new Map(originalPayloadRows.map((row) => [getMerchRowKey(row), JSON.stringify(row)]));
        const changedRows = rows.filter((row) => originalByKey.get(getMerchRowKey(row)) !== JSON.stringify(row));

        if (changedRows.length === 0) {
          setStatusByKind((prev) => ({ ...prev, [kind]: 'Nessuna modifica da salvare' }));
          return;
        }

        const params: Record<string, unknown> = { 'admin-mapping-payload': changedRows };

        await performAction({
          threadId,
          messageId,
          actionName: MAPPING_META[kind].setAction,
          params,
        });
        setStatusByKind((prev) => ({ ...prev, [kind]: `Aggiornato con successo (${changedRows.length} righe)` }));
      } else {
        const rows = rowObjects
          .map((row) => buildNegoziPayloadRow(pickEditableFields(kind, row)))
          .filter((row): row is NonNullable<ReturnType<typeof buildNegoziPayloadRow>> => row !== null);
        const originalPayloadRows = originalRows
          .map((row) => buildNegoziPayloadRow(pickEditableFields(kind, row)))
          .filter((row): row is NonNullable<ReturnType<typeof buildNegoziPayloadRow>> => row !== null);
        if (rows.length === 0) {
          setStatusByKind((prev) => ({
            ...prev,
            [kind]: 'Compila tutte le colonne richieste: gender, cod_negozio_ds, cod_shop_budget, cod_negozio_budget',
          }));
          return;
        }

        const originalByKey = new Map(originalPayloadRows.map((row) => [getNegoziRowKey(row), JSON.stringify(row)]));
        const changedRows = rows.filter((row) => originalByKey.get(getNegoziRowKey(row)) !== JSON.stringify(row));

        if (changedRows.length === 0) {
          setStatusByKind((prev) => ({ ...prev, [kind]: 'Nessuna modifica da salvare' }));
          return;
        }

        const params: Record<string, unknown> = { 'admin-mapping-payload': changedRows };

        await performAction({
          threadId,
          messageId,
          actionName: MAPPING_META[kind].setAction,
          params,
        });
        setStatusByKind((prev) => ({ ...prev, [kind]: `Aggiornato con successo (${changedRows.length} righe)` }));
      }

      setEditing((prev) => ({ ...prev, [kind]: false }));
      await loadAllMappings();
    } catch (err) {
      setStatusByKind((prev) => ({
        ...prev,
        [kind]: err instanceof Error ? err.message : 'Errore durante il salvataggio',
      }));
    } finally {
      setSaving((prev) => ({ ...prev, [kind]: false }));
    }
  };

  const deleteRow = async (kind: MappingKind, row: Record<string, unknown>, rowIdx: number, rows: Record<string, unknown>[]) => {
    if (!editing[kind]) return;
    if (isNewRow(row)) {
      updateDraft(
        kind,
        rows.filter((_, idx) => idx !== rowIdx)
      );
      setStatusByKind((prev) => ({ ...prev, [kind]: 'Riga nuova rimossa (non ancora salvata)' }));
      return;
    }

    if (!threadId || !messageId) {
      setStatusByKind((prev) => ({ ...prev, [kind]: 'Thread o messageId non disponibili.' }));
      return;
    }

    let params: Record<string, unknown> | null = null;
    if (kind === 'tipo-filato') {
      const genderValue = trimString(row.gender);
      const sourceValue = trimString(row.source_tipo_filato);
      if (!genderValue || !sourceValue) {
        setStatusByKind((prev) => ({ ...prev, [kind]: 'Impossibile eliminare: gender/source_tipo_filato mancanti' }));
        return;
      }
      params = {
        'admin-gender': genderValue,
        'admin-source-tipo-filato': sourceValue,
      };
    } else if (kind === 'merchandising-plan') {
      const genderValue = trimString(row.gender);
      const tipoFilatoDs = trimString(row.tipo_filato_ds);
      if (!genderValue || !tipoFilatoDs) {
        setStatusByKind((prev) => ({ ...prev, [kind]: 'Impossibile eliminare: gender/tipo_filato_ds mancanti' }));
        return;
      }
      params = {
        'admin-gender': genderValue,
        'admin-tipo-filato-ds': tipoFilatoDs,
      };
    } else {
      const genderValue = trimString(row.gender);
      const codNegozioDs = trimString(row.cod_negozio_ds);
      if (!genderValue || !codNegozioDs) {
        setStatusByKind((prev) => ({ ...prev, [kind]: 'Impossibile eliminare: gender/cod_negozio_ds mancanti' }));
        return;
      }
      params = {
        'admin-gender': genderValue,
        'admin-cod-negozio-ds': codNegozioDs,
      };
    }

    setDeleting((prev) => ({ ...prev, [kind]: true }));
    setStatusByKind((prev) => ({ ...prev, [kind]: '' }));
    try {
      await performAction({
        threadId,
        messageId,
        actionName: MAPPING_META[kind].deleteAction,
        params,
      });
      setStatusByKind((prev) => ({ ...prev, [kind]: 'Riga eliminata con successo' }));
      await loadAllMappings();
    } catch (err) {
      setStatusByKind((prev) => ({
        ...prev,
        [kind]: err instanceof Error ? err.message : 'Errore durante l\'eliminazione',
      }));
    } finally {
      setDeleting((prev) => ({ ...prev, [kind]: false }));
    }
  };

  const renderObjectEditor = (kind: MappingKind, value: Record<string, unknown>) => {
    const entries = Object.entries(value);
    const canEdit = editing[kind];
    return (
      <div className="space-y-2">
        {entries.map(([key, v], idx) => (
          <div key={`${key}-${idx}`} className="grid grid-cols-2 gap-2">
            <input
              value={key}
              disabled={!canEdit}
              onChange={(e) => {
                const nextEntries = [...entries];
                nextEntries[idx] = [e.target.value, v];
                updateDraft(kind, Object.fromEntries(nextEntries));
              }}
              className="border border-gray-300 px-2 py-1 text-sm disabled:bg-gray-100"
            />
            <input
              value={typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean' ? String(v) : prettyJson(v)}
              disabled={!canEdit}
              onChange={(e) => {
                const nextEntries = [...entries];
                nextEntries[idx] = [key, e.target.value];
                updateDraft(kind, Object.fromEntries(nextEntries));
              }}
              className="border border-gray-300 px-2 py-1 text-sm disabled:bg-gray-100"
            />
          </div>
        ))}
        {canEdit && (
          <button
            type="button"
            onClick={() => updateDraft(kind, { ...value, 'nuova-chiave': '' })}
            className="px-3 py-1 border border-gray-300 text-xs hover:bg-gray-50"
          >
            Aggiungi Riga
          </button>
        )}
      </div>
    );
  };

  const renderArrayEditor = (kind: MappingKind, value: unknown[]) => {
    const canEdit = editing[kind];
    const objectRows = value.every((row) => row && typeof row === 'object' && !Array.isArray(row));
    if (!objectRows) {
      return (
        <textarea
          value={drafts[kind]}
          disabled={!canEdit}
          onChange={(e) => setDrafts((prev) => ({ ...prev, [kind]: e.target.value }))}
          className="w-full min-h-[220px] max-h-[70vh] border border-gray-300 p-2 font-mono text-xs disabled:bg-gray-100"
          style={{ resize: 'vertical' }}
        />
      );
    }

    const rows = value as Record<string, unknown>[];
    const discoveredCols = Array.from(new Set(rows.flatMap((row) => Object.keys(row)))).filter((col) => !isInternalColumn(col));
    const cols = discoveredCols.length > 0 ? discoveredCols : EDITABLE_FIELDS[kind];
    const searchTerm = searchByKind[kind].trim().toLowerCase();
    const showProblematicOnly = kind === 'tipo-filato' && showProblematicByKind[kind];
    const displayedRows = rows
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => {
        if (showProblematicOnly && !(isTruthyFlag(row.is_new) && isTruthyFlag(row.is_default))) {
          return false;
        }
        if (!searchTerm) return true;
        return cols.some((col) => String(row[col] ?? '').toLowerCase().includes(searchTerm));
      });

    const isTipoFilatoAlertRow = (row: Record<string, unknown>): boolean =>
      kind === 'tipo-filato' && isTruthyFlag(row.is_new) && isTruthyFlag(row.is_default);

    return (
      <div
        className="overflow-auto border border-gray-200 min-h-[220px] max-h-[70vh]"
        style={{ resize: 'vertical' }}
      >
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {cols.map((col) => (
                <th key={col} className="px-2 py-2 text-left text-xs font-semibold text-gray-700">
                  {col}
                </th>
              ))}
              {canEdit && <th className="px-2 py-2 text-left text-xs font-semibold text-gray-700">Azioni</th>}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {displayedRows.map(({ row, index: originalIdx }) => (
              <tr
                key={originalIdx}
                className={isTipoFilatoAlertRow(row) ? 'bg-red-100' : undefined}
              >
                {cols.map((col) => (
                  <td key={col} className="px-2 py-1">
                    <input
                      value={row[col] !== undefined && row[col] !== null ? String(row[col]) : ''}
                      disabled={!canEdit || !isColumnEditable(kind, col, row)}
                      onChange={(e) => {
                        const nextRows = rows.map((r, idx) =>
                          idx === originalIdx ? { ...r, [col]: e.target.value } : r
                        );
                        updateDraft(kind, nextRows);
                      }}
                      className="w-full border border-gray-300 px-2 py-1 text-xs disabled:bg-gray-100 disabled:text-gray-600"
                    />
                  </td>
                ))}
                {canEdit && (
                  <td className="px-2 py-1 whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => deleteRow(kind, row, originalIdx, rows)}
                      disabled={deleting[kind] || saving[kind]}
                      className="px-2 py-1 border border-red-300 text-red-700 text-xs hover:bg-red-50 disabled:opacity-50"
                    >
                      Rimuovi
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {displayedRows.length === 0 && (
              <tr>
                <td colSpan={Math.max(1, cols.length + (canEdit ? 1 : 0))} className="px-3 py-4 text-sm text-gray-500">
                  Nessun risultato per il filtro corrente.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {canEdit && (
          <div className="p-2 border-t border-gray-200">
            <button
              type="button"
              onClick={() => updateDraft(kind, [...rows, buildNewRow(kind, cols)])}
              className="px-3 py-1 border border-gray-300 text-xs hover:bg-gray-50"
            >
              Aggiungi Riga
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderEditor = (kind: MappingKind) => {
    const parsed = parsedDrafts[kind];
    if (parsed === null) {
      return (
        <textarea
          value={drafts[kind]}
          disabled={!editing[kind]}
          onChange={(e) => setDrafts((prev) => ({ ...prev, [kind]: e.target.value }))}
          className="w-full min-h-[220px] max-h-[70vh] border border-red-300 p-2 font-mono text-xs disabled:bg-gray-100"
          style={{ resize: 'vertical' }}
        />
      );
    }
    if (Array.isArray(parsed)) {
      return renderArrayEditor(kind, parsed);
    }
    if (parsed && typeof parsed === 'object') {
      return renderObjectEditor(kind, parsed as Record<string, unknown>);
    }
    return (
      <textarea
        value={drafts[kind]}
        disabled={!editing[kind]}
        onChange={(e) => setDrafts((prev) => ({ ...prev, [kind]: e.target.value }))}
        className="w-full min-h-[220px] max-h-[70vh] border border-gray-300 p-2 font-mono text-xs disabled:bg-gray-100"
        style={{ resize: 'vertical' }}
      />
    );
  };

  return (
    <div className="h-full w-full bg-white overflow-auto p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between border-b border-gray-200 pb-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Admin - Mappings</h1>
            <p className="text-sm text-gray-600">Gestione mapping `tipo-filato`, `merchandising-plan`, `negozi`</p>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Filtro Gender</label>
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              className="border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">Tutti</option>
              <option value="Donna">Donna</option>
              <option value="Uomo">Uomo</option>
            </select>
          </div>
          <button
            type="button"
            onClick={loadAllMappings}
            disabled={loadingAll}
            className="px-4 py-2 border border-black text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
          >
            {loadingAll ? 'Caricamento...' : 'Ricarica Mapping'}
          </button>
          {error && <span className="text-sm text-red-600">{error}</span>}
        </div>

        {KINDS.map((kind) => (
          <section key={kind} className="border border-gray-300 bg-white">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                {MAPPING_META[kind].title}
                {kind === 'tipo-filato' && hasTipoFilatoRedRows(mappings['tipo-filato']) && (
                  <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
                    Alert
                  </span>
                )}
                {(kind === 'merchandising-plan' || kind === 'negozi') && activeMissingByKind[kind].length > 0 && (
                  <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
                    Missing {activeMissingByKind[kind].length}
                  </span>
                )}
              </h2>
              <div className="flex items-center gap-2">
                {!editing[kind] ? (
                  <button
                    type="button"
                    onClick={() => setEditing((prev) => ({ ...prev, [kind]: true }))}
                    className="px-3 py-1 border border-gray-300 text-xs hover:bg-gray-50"
                  >
                    Modifica Righe
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setEditing((prev) => ({ ...prev, [kind]: false }));
                      setDrafts((prev) => ({ ...prev, [kind]: prettyJson(mappings[kind]) }));
                    }}
                    className="px-3 py-1 border border-gray-300 text-xs hover:bg-gray-50"
                  >
                    Annulla
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => saveMapping(kind)}
                  disabled={saving[kind] || !editing[kind]}
                  className="px-3 py-1 bg-black text-white text-xs hover:bg-gray-800 disabled:opacity-50"
                >
                  {saving[kind] ? 'Salvataggio...' : 'Aggiorna'}
                </button>
              </div>
            </div>
            <div className="p-4">
              <div className="mb-3">
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="text"
                    value={searchByKind[kind]}
                    onChange={(e) => setSearchByKind((prev) => ({ ...prev, [kind]: e.target.value }))}
                    placeholder={`Cerca in ${MAPPING_META[kind].title}...`}
                    className="w-full max-w-md border border-gray-300 px-3 py-2 text-sm"
                  />
                  {kind === 'tipo-filato' && (
                    <button
                      type="button"
                      onClick={() =>
                        setShowProblematicByKind((prev) => ({
                          ...prev,
                          [kind]: !prev[kind],
                        }))
                      }
                      className={`px-3 py-2 border text-xs font-semibold ${
                        showProblematicByKind[kind]
                          ? 'border-red-600 bg-red-600 text-white'
                          : 'border-red-300 text-red-700 hover:bg-red-50'
                      }`}
                    >
                      {showProblematicByKind[kind] ? 'Mostra Tutte Le Righe' : 'Solo Righe Problematiche'}
                    </button>
                  )}
                </div>
              </div>
              {(kind === 'merchandising-plan' || kind === 'negozi') && (
                <div className="mb-3 border border-red-300 bg-red-50 p-3">
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedMissingByKind((prev) => ({
                        ...prev,
                        [kind]: !prev[kind],
                      }))
                    }
                    className="mb-2 flex w-full items-center justify-between text-left text-sm font-semibold text-red-700"
                  >
                    <span>
                      {kind === 'merchandising-plan'
                        ? `Missing tipo_filato_ds (${activeMissingByKind[kind].length})`
                        : `Missing shops (${activeMissingByKind[kind].length})`}
                    </span>
                    <span>{expandedMissingByKind[kind] ? '▲' : '▼'}</span>
                  </button>
                  {expandedMissingByKind[kind] && (
                    <div className="flex flex-col gap-2">
                      {kind === 'negozi' && (
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setNonProblematicMissingShops((prev) => {
                                const next = new Set(prev);
                                missingByKind.negozi.forEach((item) => {
                                  next.add(getMissingItemKey('negozi', item));
                                });
                                return next;
                              });
                            }}
                            className="border border-gray-400 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100"
                            disabled={missingByKind.negozi.length === 0}
                          >
                            Segna tutti non problematici
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setNonProblematicMissingShops((prev) => {
                                const next = new Set(prev);
                                missingByKind.negozi.forEach((item) => {
                                  next.delete(getMissingItemKey('negozi', item));
                                });
                                return next;
                              });
                            }}
                            className="border border-gray-400 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100"
                            disabled={ignoredMissingNegozi.length === 0}
                          >
                            Ripristina tutti problematici
                          </button>
                        </div>
                      )}
                      {activeMissingByKind[kind].length === 0 && (
                        <div className="text-xs text-gray-600">Nessun elemento da correggere.</div>
                      )}
                      {activeMissingByKind[kind].map((item, idx) => {
                        const genderValue = trimString(item.gender);
                        const missingValue = kind === 'merchandising-plan'
                          ? trimString(item.tipo_filato_ds)
                          : trimString(item.cod_negozio_ds);
                        const itemKey = getMissingItemKey(kind, item);
                        const isNonProblematic = kind === 'negozi' && nonProblematicMissingShops.has(itemKey);
                        return (
                          <div key={`${itemKey}-${idx}`} className="flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              onClick={() => appendMissingAsNewRow(kind, item)}
                              className={`border px-2 py-1 text-xs font-medium ${
                                isNonProblematic
                                  ? 'border-gray-300 bg-gray-100 text-gray-500'
                                  : 'border-red-400 bg-white text-red-700 hover:bg-red-100'
                              }`}
                              title="Clicca per aggiungere la riga in modifica"
                              disabled={isNonProblematic}
                            >
                              {genderValue} | {missingValue}
                            </button>
                            {kind === 'negozi' && (
                              <label className="flex items-center gap-1 text-xs text-gray-700">
                                <input
                                  type="checkbox"
                                  checked={isNonProblematic}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setNonProblematicMissingShops((prev) => {
                                      const next = new Set(prev);
                                      if (checked) {
                                        next.add(itemKey);
                                      } else {
                                        next.delete(itemKey);
                                      }
                                      return next;
                                    });
                                  }}
                                />
                                Non problematico
                              </label>
                            )}
                          </div>
                        );
                      })}
                      {kind === 'negozi' && ignoredMissingNegozi.length > 0 && (
                        <div className="mt-2 border-t border-gray-300 pt-2">
                          <div className="mb-1 text-xs font-semibold text-gray-700">Shop non problematici</div>
                          <div className="flex flex-wrap gap-2">
                            {ignoredMissingNegozi.map((item, idx) => {
                              const itemKey = getMissingItemKey('negozi', item);
                              return (
                                <button
                                  key={`${itemKey}-ignored-${idx}`}
                                  type="button"
                                  onClick={() => {
                                    setNonProblematicMissingShops((prev) => {
                                      const next = new Set(prev);
                                      next.delete(itemKey);
                                      return next;
                                    });
                                  }}
                                  className="border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100"
                                  title="Rendi di nuovo problematico"
                                >
                                  {trimString(item.gender)} | {trimString(item.cod_negozio_ds)} (ripristina)
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {kind === 'negozi' && nonProblematicMissingShops.size > 0 && (
                    <div className="mt-2 text-xs text-gray-600">
                      Shop marcati come non problematici: {nonProblematicMissingShops.size}
                    </div>
                  )}
                </div>
              )}
              {renderEditor(kind)}
              {statusByKind[kind] && (
                <p
                  className={`mt-2 text-xs ${
                    statusByKind[kind].toLowerCase().includes('successo') ? 'text-green-700' : 'text-red-600'
                  }`}
                >
                  {statusByKind[kind]}
                </p>
              )}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
